// Δ vs last cell renderer for the Matrix's last column.
//
// Pure presentation: takes a precomputed DeltaDescriptor and emits a
// colour-coded ▲ / ▼ / — marker. Favourable changes are coloured success;
// unfavourable changes are coloured error; flat (Δ === 0) and "no data"
// are muted.
//
// Extracted from Matrix.tsx so the per-component cognitive complexity stays
// below SonarCloud S3776 (15) and the component stays trivial to test.

import { cn } from '@/lib/utils';
import type { DeltaDescriptor } from './matrix-helpers';

interface MatrixDeltaProps {
  delta: DeltaDescriptor;
  /** True when rendered inside the org row; uses white text for legibility. */
  org?: boolean;
}

const ARROW_BY_DIRECTION = {
  up: '▲', // ▲
  down: '▼', // ▼
  flat: '—', // —
} as const;

function colourClass(delta: DeltaDescriptor, org: boolean): string {
  if (org) return 'text-white';
  if (delta.direction === null || delta.direction === 'flat') return 'text-muted-foreground';
  return delta.favourable ? 'text-success' : 'text-error';
}

function buildLabel(delta: DeltaDescriptor): string {
  if (delta.direction === null) return '—';
  if (delta.direction === 'flat') return `${ARROW_BY_DIRECTION.flat} ${delta.text}`;
  const sign = delta.direction === 'up' ? '+' : '';
  return `${ARROW_BY_DIRECTION[delta.direction]} ${sign}${delta.text}`;
}

export function MatrixDelta({ delta, org = false }: Readonly<MatrixDeltaProps>) {
  return (
    <span
      className={cn('font-bold tabular-nums whitespace-nowrap', colourClass(delta, org))}
      data-testid="matrix-delta"
      data-direction={delta.direction ?? 'none'}
    >
      {buildLabel(delta)}
    </span>
  );
}
